import {
  and,
  attachment,
  type EntryVersion,
  eq,
  journal_entry,
  journal_entry_tag,
  journal_tag,
  type PowerSyncDrizzleDB,
} from "@journal/powersync";
import { generateId } from "@fleet/utils";
import { nowISO } from "../utils/date-helpers";
import { patchRowVerified, type VerifiedWriteOptions } from "../powersync/verified-write";
import { countWords } from "./words";

/**
 * Every `UPDATE … WHERE id = ?` on `journal_entry` in this file goes through
 * `patchRowVerified`: a raw UPDATE against the PowerSync view can
 * match ZERO rows and return "success" while the newly created row is still
 * traveling to the server (see `../powersync/verified-write.ts`). These mutations
 * **may throw** (`LocalWriteLostError`); the caller must
 * surface it, never swallow it — `use-entry-mutations.tsx` already wraps each
 * one in try/catch with an error toast.
 */

export interface CreateEntryInput {
  db: PowerSyncDrizzleDB;
  userId: string;
  /** Journal day (YYYY-MM-DD, user's local time zone). */
  entryDate: string;
  body: string;
  title?: string | null;
  /** Mood 1–5 (optional). */
  mood?: number | null;
  /** Mood tags (serialized to JSON). */
  moodTags?: string[] | null;
  templateKey?: string | null;
}

export async function createEntryMutation(input: CreateEntryInput): Promise<string> {
  const { db, userId, entryDate, body, title, mood, moodTags, templateKey } = input;
  const id = generateId();
  const now = nowISO();
  await db.insert(journal_entry).values({
    id,
    user_id: userId,
    entry_date: entryDate,
    title: title?.trim() || null,
    body,
    mood: mood ?? null,
    mood_tags: moodTags && moodTags.length > 0 ? JSON.stringify(moodTags) : null,
    is_favorite: 0,
    template_key: templateKey ?? null,
    word_count: countWords(body),
    created_at: now,
    updated_at: now,
  });
  return id;
}

export interface UpdateEntryInput {
  db: PowerSyncDrizzleDB;
  entryId: string;
  entryDate?: string;
  body?: string;
  title?: string | null;
  mood?: number | null;
  moodTags?: string[] | null;
}

export async function updateEntryMutation(
  input: UpdateEntryInput,
  options?: VerifiedWriteOptions,
): Promise<void> {
  const { db, entryId, entryDate, body, title, mood, moodTags } = input;
  const patch: Record<string, unknown> = {};
  if (entryDate !== undefined) {
    patch.entry_date = entryDate;
  }
  if (body !== undefined) {
    patch.body = body;
    patch.word_count = countWords(body);
  }
  if (title !== undefined) {
    patch.title = title?.trim() || null;
  }
  if (mood !== undefined) {
    patch.mood = mood;
  }
  if (moodTags !== undefined) {
    patch.mood_tags = moodTags && moodTags.length > 0 ? JSON.stringify(moodTags) : null;
  }
  await patchRowVerified(db, journal_entry, journal_entry.id, entryId, patch, options);
}

// ─── Trash (L2) ──────────────────────────────────────────────────────────────

/**
 * Moves an entry to the trash (soft-delete): NORMAL write through the
 * write-path (PATCH deleted_at). Views filter deleted_at IS NULL, so
 * it disappears instantly; restoring = clearing the field. Never rolls back.
 */
export async function trashEntryMutation(
  db: PowerSyncDrizzleDB,
  entryId: string,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(
    db,
    journal_entry,
    journal_entry.id,
    entryId,
    { deleted_at: nowISO() },
    options,
  );
}

/** Restores an entry from the trash (deleted_at = NULL). */
export async function restoreEntryMutation(
  db: PowerSyncDrizzleDB,
  entryId: string,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(
    db,
    journal_entry,
    journal_entry.id,
    entryId,
    { deleted_at: null },
    options,
  );
}

/**
 * Permanently DELETES an entry and its local dependencies (photos and
 * tag junctions). In Postgres, FKs use ON DELETE CASCADE; local SQLite
 * has no cascade → they are deleted explicitly. Versions
 * (entry_version) cascade on the server and leave the bucket on their own.
 */
export async function deleteEntryMutation(db: PowerSyncDrizzleDB, entryId: string): Promise<void> {
  await db.delete(attachment).where(eq(attachment.entry_id, entryId));
  await db.delete(journal_entry_tag).where(eq(journal_entry_tag.entry_id, entryId));
  await db.delete(journal_entry).where(eq(journal_entry.id, entryId));
}

export async function setEntryFavoriteMutation(
  db: PowerSyncDrizzleDB,
  entryId: string,
  isFavorite: boolean,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(
    db,
    journal_entry,
    journal_entry.id,
    entryId,
    { is_favorite: isFavorite ? 1 : 0 },
    options,
  );
}

// ─── Tags ────────────────────────────────────────────────────────────────────

export async function createTagMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  name: string,
  color?: string | null,
): Promise<string> {
  const id = generateId();
  const now = nowISO();
  await db.insert(journal_tag).values({
    id,
    user_id: userId,
    name: name.trim(),
    color: color ?? null,
    created_at: now,
    updated_at: now,
  });
  return id;
}

export async function deleteTagMutation(db: PowerSyncDrizzleDB, tagId: string): Promise<void> {
  await db.delete(journal_entry_tag).where(eq(journal_entry_tag.tag_id, tagId));
  await db.delete(journal_tag).where(eq(journal_tag.id, tagId));
}

export async function addTagToEntryMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  entryId: string,
  tagId: string,
): Promise<void> {
  await db.insert(journal_entry_tag).values({
    id: generateId(),
    user_id: userId,
    entry_id: entryId,
    tag_id: tagId,
    created_at: nowISO(),
  });
}

export async function removeTagFromEntryMutation(
  db: PowerSyncDrizzleDB,
  entryId: string,
  tagId: string,
): Promise<void> {
  await db
    .delete(journal_entry_tag)
    .where(and(eq(journal_entry_tag.entry_id, entryId), eq(journal_entry_tag.tag_id, tagId)));
}

// ─── Photos ──────────────────────────────────────────────────────────────────

export interface AddEntryPhotoInput {
  db: PowerSyncDrizzleDB;
  userId: string;
  entryId: string;
  filename: string;
  url: string;
  contentType: string;
  size: number;
}

/**
 * Records the uploaded photo (the binary is already in S3 via uploadStorageObject)
 * as the entry's attachment.
 */
export async function addEntryPhotoMutation(input: AddEntryPhotoInput): Promise<string> {
  const { db, userId, entryId, filename, url, contentType, size } = input;
  const id = generateId();
  const now = nowISO();
  await db.insert(attachment).values({
    id,
    entry_id: entryId,
    user_id: userId,
    filename,
    url,
    content_type: contentType,
    size,
    created_at: now,
    updated_at: now,
  });
  return id;
}

export async function deleteEntryPhotoMutation(
  db: PowerSyncDrizzleDB,
  photoId: string,
): Promise<void> {
  await db.delete(attachment).where(eq(attachment.id, photoId));
}

// ─── Version history (L4) ────────────────────────────────────────────────────

/**
 * Restores a version: NORMAL forward write with the version's content
 * (git revert style). The write-path will archive the current state
 * as a new version in the same tx — history is never rewritten.
 */
export async function restoreEntryVersionMutation(
  db: PowerSyncDrizzleDB,
  entryId: string,
  version: Pick<EntryVersion, "entry_date" | "title" | "body" | "mood" | "mood_tags">,
  options?: VerifiedWriteOptions,
): Promise<void> {
  const body = version.body ?? "";
  await patchRowVerified(
    db,
    journal_entry,
    journal_entry.id,
    entryId,
    {
      entry_date: version.entry_date,
      title: version.title,
      body,
      mood: version.mood,
      mood_tags: version.mood_tags,
      word_count: countWords(body),
    },
    options,
  );
}
